import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Settings } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  });
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');

  useEffect(() => {
    // Don't show on admin / Aether-Login page. (Path was '/admin' historically
    // but the route was renamed to '/aether' — the check missed the new path
    // and the cookie banner appeared on the login screen.)
    if (location.pathname.startsWith('/aether') || location.pathname.startsWith('/admin')) {
      setShowBanner(false);
      setShowButton(false);
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Small delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Show the small cookie button if user already made a choice
      setShowButton(true);
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences({
          necessary: true,
          analytics: savedPrefs.analytics || false,
          marketing: savedPrefs.marketing || false
        });
      } catch (e) {
        // Invalid consent data
      }
    }
  }, [location.pathname]);

  const openCookieSettings = () => {
    setShowBanner(true);
    setShowSettings(true);
    setShowButton(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    setPreferences({ necessary: true, analytics: true, marketing: true });
    setShowBanner(false);
    setShowSettings(false);
    setShowButton(true);
  };

  const acceptNecessary = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(onlyNecessary));
    setPreferences({ necessary: true, analytics: false, marketing: false });
    setShowBanner(false);
    setShowSettings(false);
    setShowButton(true);
  };

  const savePreferences = () => {
    const customPrefs = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(customPrefs));
    setShowBanner(false);
    setShowSettings(false);
    setShowButton(true);
  };

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9998]" />
          
          {/* Cookie Banner */}
          <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl p-6 md:p-8 liquid-glass-strong">
                {!showSettings ? (
                  // Main Banner
                  <>
                    <div className="flex items-start gap-4 mb-6">
                      <div 
                        className="p-3 rounded-xl"
                        style={{ background: 'rgba(249, 115, 22, 0.15)' }}
                      >
                        <Cookie className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-syne text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {t('cookie.title')}
                        </h3>
                        <p className="font-manrope text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                          {t('cookie.description')}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={acceptAll}
                        className="flex-1 px-6 py-3 rounded-full font-manrope font-semibold text-black transition-all duration-300 hover:scale-[1.02]"
                        style={{ 
                          background: 'var(--primary)',
                          boxShadow: '0 0 20px rgba(249, 115, 22, 0.3)'
                        }}
                      >
                        {t('cookie.acceptAll')}
                      </button>
                      <button
                        onClick={acceptNecessary}
                        className="flex-1 px-6 py-3 rounded-full font-manrope font-semibold transition-all duration-300 hover:scale-[1.02] liquid-glass"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {t('cookie.necessaryOnly')}
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        className="flex-1 px-6 py-3 rounded-full font-manrope font-semibold transition-all duration-300 hover:scale-[1.02] liquid-glass"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {t('cookie.settings')}
                      </button>
                    </div>

                    <p className="font-manrope text-xs mt-4 text-center" style={{ color: 'var(--text-muted)' }}>
                      {t('cookie.moreInfo')}{' '}
                      <a href={isEnglish ? '/en/privacy' : '/datenschutz'} className="hover:underline" style={{ color: 'var(--primary)' }}>
                        {t('cookie.privacyLink')}
                      </a>.
                    </p>
                  </>
                ) : (
                  // Settings View
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-syne text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        {t('cookie.title')}
                      </h3>
                      <button
                        onClick={() => {
                          setShowSettings(false);
                          // If user already had consent, close the banner
                          const consent = localStorage.getItem('cookie_consent');
                          if (consent) {
                            setShowBanner(false);
                            setShowButton(true);
                          }
                        }}
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 liquid-glass"
                      >
                        <X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                      </button>
                    </div>

                    <div className="space-y-4 mb-6">
                      {/* Necessary Cookies */}
                      <div className="p-4 rounded-xl liquid-glass">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-green-500" />
                            <div>
                              <h4 className="font-manrope font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {t('cookie.necessary')}
                              </h4>
                              <p className="font-manrope text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {t('cookie.necessaryDesc')}
                              </p>
                            </div>
                          </div>
                          <div 
                            className="px-3 py-1 rounded-full text-xs font-manrope font-medium"
                            style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' }}
                          >
                            {t('cookie.alwaysActive')}
                          </div>
                        </div>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="p-4 rounded-xl liquid-glass">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <BarChart3 className="w-5 h-5 text-blue-500" />
                            <div>
                              <h4 className="font-manrope font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {t('cookie.analytics')}
                              </h4>
                              <p className="font-manrope text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {t('cookie.analyticsDesc')}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences.analytics}
                              onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                              className="sr-only peer"
                            />
                            <div 
                              className="w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"
                              style={{ background: preferences.analytics ? 'var(--primary)' : 'rgba(255, 255, 255, 0.1)' }}
                            ></div>
                          </label>
                        </div>
                      </div>

                      {/* Marketing Cookies */}
                      <div className="p-4 rounded-xl liquid-glass">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Cookie className="w-5 h-5 text-purple-500" />
                            <div>
                              <h4 className="font-manrope font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {t('cookie.marketing')}
                              </h4>
                              <p className="font-manrope text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {t('cookie.marketingDesc')}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences.marketing}
                              onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                              className="sr-only peer"
                            />
                            <div 
                              className="w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"
                              style={{ background: preferences.marketing ? 'var(--primary)' : 'rgba(255, 255, 255, 0.1)' }}
                            ></div>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Wie im ersten Schritt: auf dem Handy untereinander, Beschriftung nie umbrechen
                        („Save Settings“ brach sonst zweizeilig um) */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={savePreferences}
                        className="flex-1 px-6 py-3 rounded-full font-manrope font-semibold whitespace-nowrap text-black transition-all duration-300 hover:scale-[1.02]"
                        style={{ 
                          background: 'var(--primary)',
                          boxShadow: '0 0 20px rgba(249, 115, 22, 0.3)'
                        }}
                      >
                        {t('cookie.saveSettings')}
                      </button>
                      <button
                        onClick={acceptAll}
                        className="flex-1 px-6 py-3 rounded-full font-manrope font-semibold whitespace-nowrap transition-all duration-300 hover:scale-[1.02] liquid-glass"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {t('cookie.acceptAll')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CookieBanner;
